import { useEffect, useRef, useState } from 'react'
import { TextField } from '@kcm/ui'
import type { AddressPlaceResponse, AddressSuggestion } from '@kcm/contracts'
import { endpoints } from '../lib/endpoints'

/**
 * The street-address field with server-backed suggestions (Google Places,
 * proxied — no SDK, no key in this bundle).
 *
 * It is an ASSIST, structurally: the field stays a plain, fully typable
 * text input, every network failure degrades to "no suggestions", and
 * nothing here can block submission. The clientele types in Thai, Vietnamese
 * and Chinese as often as in Latin script; the API passes their locale to
 * Google, so suggestions come back in the script they are typing.
 *
 * One `sessionToken` covers a typing run plus its concluding pick — that is
 * Google's per-session billing unit — and a fresh one is minted after each
 * pick.
 */

const DEBOUNCE_MS = 300
const MIN_CHARS = 3

export interface AddressAutocompleteProps {
  value: string
  onChange: (text: string) => void
  /** A completed pick, already split into the form's three fields. */
  onPick: (place: AddressPlaceResponse) => void
  disabled?: boolean
  error?: string | undefined
  hint?: string
}

export function AddressAutocomplete({
  value,
  onChange,
  onPick,
  disabled,
  error,
  hint,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const session = useRef<string>(crypto.randomUUID())
  const debounce = useRef<ReturnType<typeof setTimeout>>()
  const abort = useRef<AbortController>()
  // Distinguishes "value changed because the user typed" from "value changed
  // because a pick filled it" — only the former should query.
  const typed = useRef(false)

  useEffect(() => {
    if (!typed.current) return
    typed.current = false

    clearTimeout(debounce.current)
    abort.current?.abort()
    if (value.trim().length < MIN_CHARS) {
      setSuggestions([])
      setOpen(false)
      return
    }
    debounce.current = setTimeout(() => {
      const controller = new AbortController()
      abort.current = controller
      endpoints.address
        .suggest(value.trim(), session.current, controller.signal)
        .then((res) => {
          setSuggestions(res.suggestions)
          setOpen(res.suggestions.length > 0)
          setActive(-1)
        })
        .catch(() => {
          // Silence is the correct failure mode for an assist.
          setSuggestions([])
          setOpen(false)
        })
    }, DEBOUNCE_MS)

    return () => clearTimeout(debounce.current)
  }, [value])

  const pick = (s: AddressSuggestion) => {
    setOpen(false)
    setSuggestions([])
    const token = session.current
    session.current = crypto.randomUUID()
    // Optimistic: the main text lands in the field immediately, and the split
    // fields follow when the lookup answers. If it never does, the user has
    // the suggestion text and two fields to type — still ahead of no assist.
    onChange(s.main)
    endpoints.address
      .place(s.placeId, token)
      .then(onPick)
      .catch(() => {})
  }

  return (
    <div className="relative">
      <TextField
        required
        label="Street address"
        name="street-address"
        autoComplete="street-address"
        role="combobox"
        aria-expanded={open}
        aria-controls="address-suggestions"
        aria-autocomplete="list"
        value={value}
        onChange={(e) => {
          typed.current = true
          onChange(e.target.value)
        }}
        onBlur={() => {
          // Delayed so a click on a suggestion wins the race against blur.
          setTimeout(() => setOpen(false), 150)
        }}
        onKeyDown={(e) => {
          if (!open) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => Math.min(i + 1, suggestions.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            // Only intercept while the list is open — otherwise Enter submits
            // the form exactly as it would in a plain field.
            e.preventDefault()
            const chosen = suggestions[active >= 0 ? active : 0]
            if (chosen) pick(chosen)
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
        disabled={disabled}
        error={error}
        hint={hint ?? 'The address on your bank statement or utility bill.'}
      />
      {open && (
        <ul
          id="address-suggestions"
          role="listbox"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line-strong bg-white shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li key={s.placeId} role="option" aria-selected={i === active}>
              <button
                type="button"
                className={`w-full px-3 py-2 text-left text-sm ${
                  i === active ? 'bg-surface-alt' : 'hover:bg-surface-alt'
                }`}
                // Fires before the input's delayed blur closes the list.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(s)
                }}
              >
                <span className="font-medium">{s.main}</span>
                {s.secondary && <span className="text-muted"> — {s.secondary}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
